import { useEffect, useRef } from 'react';
import { trackEvent } from '../utils/analytics';

/**
 * Track user engagement through scroll depth and activity
 */
export const useEngagementTracking = () => {
  const scrollDepthTracked = useRef({ 25: false, 50: false, 75: false, 90: false });
  const lastActivityTime = useRef(Date.now());
  const engagementStartTime = useRef(Date.now());

  useEffect(() => {
    // Track scroll depth
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollPercent = (scrollTop / (documentHeight - windowHeight)) * 100;

      // Track scroll milestones
      if (scrollPercent >= 25 && !scrollDepthTracked.current[25]) {
        scrollDepthTracked.current[25] = true;
        trackEvent('scroll', { scroll_depth: 25 });
      }
      if (scrollPercent >= 50 && !scrollDepthTracked.current[50]) {
        scrollDepthTracked.current[50] = true;
        trackEvent('scroll', { scroll_depth: 50 });
      }
      if (scrollPercent >= 75 && !scrollDepthTracked.current[75]) {
        scrollDepthTracked.current[75] = true;
        trackEvent('scroll', { scroll_depth: 75 });
      }
      if (scrollPercent >= 90 && !scrollDepthTracked.current[90]) {
        scrollDepthTracked.current[90] = true;
        trackEvent('scroll', { scroll_depth: 90 });
      }

      // Update last activity time
      lastActivityTime.current = Date.now();
    };

    // Track user activity (clicks, keypresses, mouse movement)
    const handleUserActivity = () => {
      lastActivityTime.current = Date.now();
    };

    // Track time on page and send engagement events
    const engagementInterval = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivityTime.current;
      const totalTimeOnPage = Date.now() - engagementStartTime.current;

      // If user is active (activity within last 30 seconds), track engagement
      if (timeSinceLastActivity < 30000 && totalTimeOnPage >= 10000) {
        // Send engagement_time event every 10 seconds if user is active
        if (totalTimeOnPage % 10000 < 1000) {
          trackEvent('user_engagement', {
            engagement_time_msec: totalTimeOnPage,
            engaged_session: true,
          });
        }
      }
    }, 1000);

    // Track page visibility changes
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Page is hidden, pause engagement tracking
        const timeOnPage = Date.now() - engagementStartTime.current;
        if (timeOnPage > 1000) {
          trackEvent('user_engagement', {
            engagement_time_msec: timeOnPage,
            page_visible: false,
          });
        }
      } else {
        // Page is visible again, resume tracking
        engagementStartTime.current = Date.now();
        lastActivityTime.current = Date.now();
      }
    };

    // Add event listeners
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('click', handleUserActivity, { passive: true });
    window.addEventListener('keydown', handleUserActivity, { passive: true });
    window.addEventListener('mousemove', handleUserActivity, { passive: true });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Track initial engagement
    trackEvent('user_engagement', {
      engagement_time_msec: 0,
      engaged_session: true,
    });

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleUserActivity);
      window.removeEventListener('keydown', handleUserActivity);
      window.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(engagementInterval);

      // Send final engagement time on unmount
      const finalTime = Date.now() - engagementStartTime.current;
      if (finalTime > 1000) {
        trackEvent('user_engagement', {
          engagement_time_msec: finalTime,
          engaged_session: true,
          page_exit: true,
        });
      }
    };
  }, []);
};
